/**
 * completedFormPdf.ts
 *
 * Download and Print for a completed e-form's PDF, shared by the patient
 * chart's response modal and the forms inbox modal. The server builds the
 * PDF on request (`GET .../survey-response/{id}/pdf` and
 * `GET /forms/inbox/responses/{id}/pdf`); nothing here is stored.
 *
 * Errors arrive as JSON `{ error: { code, message } }`, but because the
 * request asks for a blob the body is a Blob and has to be read before the
 * code is known. Staff only ever see the messages below: the size limit
 * (413 TOO_LARGE) and a form that is gone (404) have their own, everything
 * else gets the general one.
 */
import axios from 'axios';
import { saveBlob } from '@/services/standardReports.service';

export const COMPLETED_FORM_PDF_ERROR =
  "Couldn't prepare this form. Try again, and contact support if it keeps happening.";
export const COMPLETED_FORM_PDF_TOO_LARGE =
  'This form is too large to download here. Contact support for a copy.';
export const COMPLETED_FORM_PDF_GONE =
  'This form has been moved or deleted. Close this window and refresh the list to find it.';
export const COMPLETED_FORM_PDF_POPUP_BLOCKED =
  'Your browser blocked the print window. Allow pop-ups for this site, then press Print again.';

/** How long the print tab keeps the file's object URL before it is freed. */
const PRINT_URL_LIFETIME_MS = 60_000;

export interface CompletedFormPdf {
  blob: Blob;
  /** The server's file name, or null when the header could not be read (it is not exposed cross-origin). */
  fileName: string | null;
}

function parseFileName(contentDisposition: unknown): string | null {
  if (typeof contentDisposition !== 'string' || contentDisposition === '') return null;
  const quoted = contentDisposition.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const plain = contentDisposition.match(/filename=([^;]+)/i);
  const name = plain?.[1]?.trim() ?? '';
  return name !== '' ? name : null;
}

/** The blob and server file name from a `responseType: 'blob'` response. */
export function toCompletedFormPdf(response: { data: Blob; headers: unknown }): CompletedFormPdf {
  const headers = response.headers as { get?: (name: string) => unknown } & Record<string, unknown>;
  const disposition =
    typeof headers?.get === 'function' ? headers.get('content-disposition') : headers?.['content-disposition'];
  return { blob: response.data, fileName: parseFileName(disposition) };
}

/** The request options both PDF endpoints are called with. */
export const COMPLETED_FORM_PDF_REQUEST = {
  responseType: 'blob' as const,
  headers: { Accept: 'application/pdf' },
};

/**
 * "<Form-title>-<YYYY-MM-DD>.pdf", built the way the server builds its own
 * name (EFormPdfCopy::fileName), for when that name cannot be read.
 */
export function completedFormPdfFileName(title: string, date: string | null): string {
  const slug = title.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
  const day = (date ?? '').slice(0, 10);
  return `${slug !== '' ? slug : 'Form'}${/^\d{4}-\d{2}-\d{2}$/.test(day) ? `-${day}` : ''}.pdf`;
}

async function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function errorCode(data: unknown): Promise<string | null> {
  let body: unknown = data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      body = JSON.parse(await readBlobText(data));
    } catch {
      return null;
    }
  }
  const code = (body as { error?: { code?: unknown } } | null)?.error?.code;
  return typeof code === 'string' ? code : null;
}

/** The message staff see for a failed PDF request. Never a status code. */
export async function completedFormPdfErrorMessage(err: unknown): Promise<string> {
  if (!axios.isAxiosError(err) || !err.response) return COMPLETED_FORM_PDF_ERROR;
  if (err.response.status === 413) return COMPLETED_FORM_PDF_TOO_LARGE;
  // Retrying cannot help here: the form left this chart or was deleted while open.
  if (err.response.status === 404) return COMPLETED_FORM_PDF_GONE;
  return (await errorCode(err.response.data)) === 'TOO_LARGE' ? COMPLETED_FORM_PDF_TOO_LARGE : COMPLETED_FORM_PDF_ERROR;
}

async function fetchOrExplain(fetchPdf: () => Promise<CompletedFormPdf>): Promise<CompletedFormPdf> {
  try {
    return await fetchPdf();
  } catch (err) {
    throw new Error(await completedFormPdfErrorMessage(err));
  }
}

/**
 * Fetch the PDF and save it, under the server's name when it can be read and
 * `fallbackName` otherwise. Rejects with an Error whose message is for staff.
 */
export async function downloadCompletedFormPdf(
  fetchPdf: () => Promise<CompletedFormPdf>,
  fallbackName: string,
): Promise<void> {
  const pdf = await fetchOrExplain(fetchPdf);
  // saveBlob revokes its object URL once the download has started.
  saveBlob(pdf.blob, pdf.fileName ?? fallbackName);
}

/**
 * Open the tab Print shows the PDF in. Call it straight from the click
 * handler, before any await: a window opened later is blocked as a pop-up.
 * Null when the browser blocked it.
 */
export function openPrintWindow(): Window | null {
  return window.open('', '_blank');
}

/**
 * Fetch the PDF into a tab opened by `openPrintWindow`, where staff print it
 * from the browser's PDF viewer. On failure the tab is closed and this rejects
 * with an Error whose message is for staff.
 */
export async function showCompletedFormPdfInWindow(
  win: Window,
  fetchPdf: () => Promise<CompletedFormPdf>,
): Promise<void> {
  let pdf: CompletedFormPdf;
  try {
    pdf = await fetchOrExplain(fetchPdf);
  } catch (err) {
    win.close();
    throw err;
  }
  const url = URL.createObjectURL(pdf.blob);
  win.location.href = url;
  // The tab needs the URL while it loads; free it once it has had time to.
  setTimeout(() => URL.revokeObjectURL(url), PRINT_URL_LIFETIME_MS);
}
